import {
  BoExportConfig,
  BoViewType,
  BookingOrder,
  BookingOrderAggregation,
  BookingOrderAuditEventResponse,
  BookingOrderRequest,
  BookingOrderVersion,
  MediaTypeFilters,
  SBSAdditionalDetails,
} from "models/bookingOrder"
import { Notification, NotificationResponse } from "models/notifications"
import { http, httpExport, httpNoTimeout } from "./http"

import { ChangedPlanRecord } from "routes/Base/Plans/Details/types"
import { MbaConfiguration } from "models/mba"
import { MediaPlanSum } from "models/mediaPlan"
import { Paginated } from "./types"
import { FF_CANCEL_BO } from "../routes/Base/BookingOrders/constants"
import {
  CancelBookingOrderType,
  CancellationData,
} from "models/bookingOrdersCancellation"

/** FILTERS **/

export const fetchMPFilters = async (_key: string, id: string) => {
  const res = await http.get(`media-plans/${id}/filters`).json()
  return res as MediaTypeFilters
}

/** BOOKING ORDERS **/

export const fetchBookingOrders = async (_key: string, params?: string) => {
  const searchParams = new URLSearchParams(params)
  searchParams.append("includeCancelled", FF_CANCEL_BO.toString())
  const res = await httpNoTimeout.get("booking-orders", { searchParams }).json()
  return res as Paginated<BookingOrder>
}

export const fetchBookingOrdersAggregations = async (_key: string) => {
  const res = await httpNoTimeout("booking-orders/aggregations").json()
  return res as BookingOrderAggregation
}

export const fetchBookingOrder = async (
  _key: string,
  id: string,
  version: string | null,
  viewType?: BoViewType,
) => {
  const searchParams = new URLSearchParams()
  !!version && searchParams.set("version", version)
  !!viewType && searchParams.set("viewType", viewType)
  const res = await http.get(`booking-orders/${id}`, { searchParams }).json()
  return res as BookingOrder
}

export const createBookingOrder = async (pld: BookingOrderRequest) =>
  await http.post("booking-orders", { json: pld })

export const updateBookingOrder = async (pld: {
  id: string
  name?: string
  contractNumber?: string
}) => {
  const { id, ...json } = pld
  const res = await http.patch(`booking-orders/${id}`, { json }).json()
  return res as BookingOrder
}

export const postBookingOrder = async (pld: { id: string }) => {
  const { id } = pld
  const res = await httpNoTimeout.post(`booking-orders/${id}/post`).json()
  return res as BookingOrder
}

export const revertPostBookingOrder = async (pld: { id: string }) => {
  const { id } = pld
  const res = await http.post(`booking-orders/${id}/revert-post`).json()
  return res as BookingOrder
}

export const postToIsapBookingOrder = async (pld: { id: string }) => {
  const { id } = pld
  const res = await httpNoTimeout
    .post(`booking-orders/${id}/post-to-isap`)
    .json()
  return res as BookingOrder
}

export const deleteBookingOrder = async (id: string) =>
  await http.delete(`booking-orders/${id}`)

export const addBookingOrderLines = async (pld: {
  id: string
  packageIds: string[]
  lineItemIds: string[]
}) => {
  const { id, ...json } = pld
  return await http.post(`booking-orders/${id}/items`, { json })
}

export const deleteBookingOrderLines = async (pld: {
  id: string
  packageIds: string[]
  lineItemIds: string[]
}) => {
  const { id, ...json } = pld
  return await http.delete(`booking-orders/${id}/items`, { json })
}

export const postBookingOrderLines = async (pld: {
  id: string
  packageIds: string[]
  lineItemIds: string[]
}) => {
  const { id, ...json } = pld
  return await httpNoTimeout.post(`booking-orders/${id}/post-items`, { json })
}

export const revertPostBookingOrderLines = async (pld: {
  id: string
  packageIds: string[]
  lineItemIds: string[]
}) => {
  const { id, ...json } = pld
  return await http.post(`booking-orders/${id}/revert-post-items`, { json })
}

type SplitFrames = {
  timeFrames: Array<{ start: string; end: string }>
}

export const splitBookingOrderLineItem = async ({
  id,
  liId,
  pld,
}: {
  id: string
  liId: string
  pld: SplitFrames
}) => {
  return await http.post(`booking-orders/${id}/line-items/${liId}/split`, {
    json: { ...pld },
  })
}

export const getBookingOrderSplitOptions = async (
  _key: string,
  id: string,
  liId: string,
  params: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http(
    `booking-orders/${id}/line-items/${liId}/splitFrames`,
    {
      searchParams,
    },
  ).json()
  return res as SplitFrames
}

/** REVISIONS **/

export const saveBORevision = async (payload: {
  id: string
  flowPlanItems?: ChangedPlanRecord[]
  viewType?: BoViewType
}) => {
  const searchParams = new URLSearchParams()
  const { id, flowPlanItems, viewType } = payload
  !!viewType && searchParams.set("viewType", viewType)
  const json = { flowPlanItems }
  await http.post(`booking-orders/${id}/revision/save`, {
    json,
    searchParams,
  })
}

export const submitBORevision = async (payload: {
  id: string
  flowPlanItems?: ChangedPlanRecord[]
  viewType?: BoViewType
  comment?: string
}) => {
  const searchParams = new URLSearchParams()
  const { id, flowPlanItems, viewType, comment } = payload
  !!viewType && searchParams.set("viewType", viewType)
  const json = { mediaPlan: { flowPlanItems }, comment }
  await http.post(`booking-orders/${id}/revision/submit`, {
    json,
    searchParams,
  })
}

export const approveBORevision = async (payload: {
  id: string
  clientApprover?: string
  viewType?: "CLIENT_AGENCY" | "INTERCOMPANY"
  comment?: string
  approvalDate?: string
}) => {
  const searchParams = new URLSearchParams()
  const { id, viewType, ...json } = payload
  !!viewType && searchParams.set("viewType", viewType)
  return await http.post(`booking-orders/${id}/revision/approve`, {
    json,
    searchParams,
  })
}

export const rejectBORevision = async (payload: {
  id: string
  comment: string
  viewType?: "CLIENT_AGENCY" | "INTERCOMPANY"
}) => {
  const searchParams = new URLSearchParams()
  const { id, viewType, ...json } = payload
  !!viewType && searchParams.set("viewType", viewType)
  return await http.post(`booking-orders/${id}/revision/comment`, {
    json,
    searchParams,
  })
}

export const discardBORevision = async (payload: {
  id: string
  viewType?: BoViewType
}) => {
  const searchParams = new URLSearchParams()
  const { id, viewType } = payload
  !!viewType && searchParams.set("viewType", viewType)
  return await http.post(`booking-orders/${id}/revision/discard`, {
    searchParams,
  })
}

/** FINANCIAL **/

export const fetchBOSummary = async (
  _key: string,
  id: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http(`booking-orders/${id}/financial-sum`, {
    searchParams,
  }).json()
  return res as MediaPlanSum
}

/** ACTIVITY **/

export const fetchBOActivity = async (
  _key: string,
  id: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params || undefined)
  const res = (await http(`booking-orders/${id}/audit`, {
    searchParams,
  }).json()) as BookingOrderAuditEventResponse

  const notificationsMapped = res?.data?.map((d) => ({
    id: d?.id,
    actorId: d?.actor?.id,
    actorFirstName: d?.actor?.firstName,
    actorLastName: d?.actor?.lastName,
    notificationEvent: d?.action,
    dateCreated: new Date(d?.timestamp).getTime(),
    dateModified: new Date(d?.timestamp).getTime(),
  })) as Partial<Notification>[]

  const responseMapped = {
    ...res,
    data: notificationsMapped,
  } as NotificationResponse

  return responseMapped
}

export const acknowledgeBOLineItem = async ({
  boId,
  id,
  type,
  viewType,
}: {
  boId: string
  id: string
  type: string
  viewType?: BoViewType
}) => {
  const searchParams = new URLSearchParams()
  searchParams.set("type", type)
  !!viewType && searchParams.set("viewType", viewType)
  const res = await http.post(
    `booking-orders/${boId}/flow-plan-items/${id}/acknowledge`,
    {
      searchParams,
    },
  )

  return res
}

export const fetchBoExportConfig = async (_key: string, id: string) => {
  const searchParams = new URLSearchParams({ bookingOrderId: id })
  const res = await http(`bo-export-config`, { searchParams }).json()

  return res as BoExportConfig
}

export const patchBoExportConfig = async (
  payload: Partial<MbaConfiguration>,
) => {
  const { id, ...json } = payload
  return await http.patch(`bo-export-config/${id}`, { json })
}

export const patchSBSAdditionalDetails = async (
  payload: { id: string } & Partial<SBSAdditionalDetails>,
) => {
  const { id, ...json } = payload
  return await http.patch(`booking-orders/${id}/aa-options`, { json })
}

export const exportBookingOrder = async (
  bookingOrderId: string,
  payload?: {
    contact?: string
    intercompany?: boolean
    remarks?: string
    showLineItemName?: boolean
    showSpotDetails?: boolean
  },
  viewType?: BoViewType,
) => {
  const searchParams = new URLSearchParams()
  !!viewType && searchParams.set("viewType", viewType)
  return httpExport
    .post(`${bookingOrderId}/bo`, {
      timeout: false,
      json: payload,
      searchParams,
    })
    .blob()
}

export const exportBookingOrderAa = async (
  bookingOrderId: string,
  payload?: {
    contact?: string
    intercompany?: boolean
    remarks?: string
    showLineItemName?: boolean
    showSpotDetails?: boolean
    version?: number
  },
  viewType?: BoViewType,
) => {
  const searchParams = new URLSearchParams()
  !!viewType && searchParams.set("viewType", viewType)
  return http
    .post(`booking-orders/${bookingOrderId}/export-aa`, {
      timeout: false,
      json: payload,
      searchParams,
    })
    .blob()
}

export const exportBookingOrderSBS = async (
  bookingOrderId: string,
  // payload?: {
  //   contact?: string
  //   intercompany?: boolean
  //   remarks?: string
  //   showLineItemName?: boolean
  // },
  // viewType?: BoViewType,
) => {
  const res = await httpExport.get(`${bookingOrderId}/sbs`, { timeout: false })
  return {
    blob: await res.blob(),
    filename: res.headers.get("content-disposition")!.split("filename=")[1],
  }
}

/** VERSIONS **/

export const fetchBookingOrderVersions = async (_key: string, id: string) => {
  const { data } = await http(`booking-orders/${id}/versions`).json<{
    data: Array<BookingOrderVersion>
  }>()
  return data
}

export const patchBookingOrderVersionComment = async ({
  id,
  comment,
}: {
  id: string
  comment: string
}) => {
  const res = await http
    .patch(`booking-orders/audit/${id}/comment`, { json: { comment } })
    .json()
  return res as BookingOrderVersion
}

export const markOfficialBookingOrder = async (pld: { id: string }) => {
  const { id } = pld
  const res = await http.post(`booking-orders/${id}/mark-official`)
  return res
}

export const cancelBookingOrder = async (
  cancellationData: CancelBookingOrderType,
) => {
  const {
    bookingOrderId,
    cancelledBy,
    cancellationDate,
    cancellationComment,
    cancellationFiles,
  } = cancellationData
  return http.post(`booking-orders/cancel`, {
    json: {
      bookingOrderId,
      cancelledBy,
      cancellationDate,
      cancellationComment,
      cancellationFiles,
    },
  })
}

export const getBookingOrderCancellationDetails = async (
  _key: string,
  bookingOrderId: string,
) => {
  const res = await http.get(`booking-orders/${bookingOrderId}/cancel`).json()
  return res as CancellationData
}

export const downloadCancelledBookingOrder = async (bookingOrderId: string) => {
  return http
    .post(`bo-export-cancelled/${bookingOrderId}`, {
      json: {
        contact: "",
        remarks: "",
        showLineItemName: true,
        showSpotDetails: true,
      },
    })
    .blob()
}
